import React, { useEffect, useReducer, useState } from 'react';
import { useAuth } from '../../Context';

import { Empty, Row, Col, Typography, Button, Space, Table, Form, Modal, Spin, Tooltip, message, Popover, Avatar, Card, Select, InputNumber, DatePicker, Tag } from 'antd';
import { Content } from 'antd/es/layout/layout';
import { PlusOutlined, EditOutlined, DeleteOutlined, InfoOutlined, AppstoreOutlined, EyeOutlined } from '@ant-design/icons';

import {
	DETECTIONCLUSTER_LIST, DETECTIONCLUSTER_LIST_SUCCESS, DETECTIONCLUSTER_LIST_ERROR,
	DETECTIONCLUSTER_DETAILS, DETECTIONCLUSTER_DETAILS_SUCCESS, DETECTIONCLUSTER_DETAILS_ERROR,
	ADD_DETECTIONCLUSTER, ADD_DETECTIONCLUSTER_SUCCESS, ADD_DETECTIONCLUSTER_ERROR,
	UPDATE_DETECTIONCLUSTER, UPDATE_DETECTIONCLUSTER_SUCCESS, UPDATE_DETECTIONCLUSTER_ERROR,
	DELETE_DETECTIONCLUSTER, DELETE_DETECTIONCLUSTER_SUCCESS, DELETE_DETECTIONCLUSTER_ERROR,
	DETECTIONALG_LIST, DETECTIONALG_LIST_SUCCESS, DETECTIONALG_LIST_ERROR
} from './constants';

import detectionClusterReducer from './reducer';
import englan from '../../../english.json';
import germanlan from '../../../german.json';
import arabiclan from '../../../arabic.json';
import frenchlan from '../../../french.json';
import hindilan from '../../../hindi.json';
import malayalamlan from '../../../malayalam.json';

import axios from 'axios';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;

// status is a free TEXT column on the backend - adjust these to the values you actually use
const STATUS_OPTIONS = ['New', 'Failed', 'Completed'].map((v) => ({ value: v, label: v }));

const DEFAULT_PIDS_ID = 1;

export default function DetectionCluster() {
	const [addForm] = Form.useForm();
	const [editForm] = Form.useForm();
	const navigate = useNavigate();

	const dashboardtype = process.env.REACT_APP_IDENTIFIER;
	const prefix = dashboardtype.toUpperCase();
	const authtoken = localStorage.getItem(`${prefix}_ACCESS_TOKEN`);
	const permission = localStorage.getItem(`${prefix}_PERMISSION`);
	const isAdmin = permission === 'superadmin' || permission === 'admin';

	const apiUrl = localStorage.getItem(`${prefix}_API_URL`);
	const api = apiUrl ? apiUrl : process.env.REACT_APP_CORE_HOST;

	const langid = localStorage.getItem(`${prefix}_LANGUAGE_ID`) ? parseInt(localStorage.getItem(`${prefix}_LANGUAGE_ID`)) : 2;
	const langdata = langid == 1 ? arabiclan : langid == 2 ? englan : langid == 3 ? frenchlan : langid == 4 ? germanlan : langid == 5 ? hindilan : langid == 6 ? malayalamlan : englan;
	const t = (key, fallback) => (langdata && langdata[key] ? langdata[key] : fallback);

	const envTimeout = process.env.REACT_APP_TIMEOUT == undefined || process.env.REACT_APP_TIMEOUT == 'undefined' ? 5000 : process.env.REACT_APP_TIMEOUT;
	const timeout = localStorage.getItem(`${prefix}_TIMEOUT`) == 'null' || localStorage.getItem(`${prefix}_TIMEOUT`) == 'undefined' ? parseInt(envTimeout) : parseInt(localStorage.getItem(`${prefix}_TIMEOUT`));
	const pipelineId = localStorage.getItem(`${prefix}_pipelineId`) ? localStorage.getItem(`${prefix}_pipelineId`) : 1;
	const featuresModule = JSON.parse(localStorage.getItem(`${dashboardtype.toUpperCase()}_FEATURES_MODULE_DASHBOARD`));

	const { pidsData, getPids, channelData, getChannels } = useAuth();

	const initialState = {
		clusters: [],
		details: null,
		detalg: [],
		pending: false,
		loading: false,
		error: false,
		pagination: { total: 0, current: 1, pageSize: 10 },
	};
	const [data, dispatch] = useReducer(detectionClusterReducer, initialState);

	// modals
	const [showAddModal, setShowAddModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);

	// filters. pidsinfoId is ALWAYS sent (backend picks the table from it), so it defaults to 1 and has no "ALL" option
	const [pidsFilter, setPidsFilter] = useState(DEFAULT_PIDS_ID);
	const [channelFilter, setChannelFilter] = useState(null);
	const [algFilter, setAlgFilter] = useState(null);
	const [statusFilter, setStatusFilter] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);

	// ---------------------------------------------------------------- helpers
	const showError = (error) => {
		message.config({ duration: 5, maxCount: 1 });
		if (error?.code == 'ECONNABORTED' || error?.code == 'ERR_NETWORK') {
			message.open({ type: 'error', content: t('ItseemsthenetworkisunreachablePleaserefreshtheapplication', 'It seems the network is unreachable. Please refresh the application.') });
		} else {
			message.open({ type: 'error', content: error?.response?.data?.message || t('SomethingWentWrong', 'Something Went Wrong!') });
		}
	};

	const request = (method, url, body) =>
		axios.request({
			method,
			maxBodyLength: Infinity,
			url: `${api}${url}`,
			timeout,
			headers: {
				accept: 'application/json',
				'Content-Type': 'application/json',
				Authorization: `Bearer ${authtoken}`,
			},
			data: body,
		});

	const filterOption = (input, option) => (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase());

	// ---------------------------------------------------------------- API
	const loadClusters = (page = currentPage, size = pageSize) => {
		const parts = [
			`pidsinfoId=${pidsFilter || DEFAULT_PIDS_ID}`,
			'$sort[id]=-1',
			`$limit=${size}`,
			`$skip=${(page - 1) * size}`,
		];
		if (channelFilter) parts.push(`channelId=${channelFilter}`);
		if (algFilter) parts.push(`detectionalgorithmtypeId=${algFilter}`);
		if (statusFilter) parts.push(`status=${encodeURIComponent(statusFilter)}`);

		dispatch({ type: DETECTIONCLUSTER_LIST });

		request('get', `/detectioncollections?${parts.join('&')}`)
			.then((response) => {
				if (response.status === 200) {
					dispatch({ type: DETECTIONCLUSTER_LIST_SUCCESS, payload: response.data, page, pageSize: size });
				} else {
					dispatch({ type: DETECTIONCLUSTER_LIST_ERROR });
				}
			})
			.catch((error) => {
				dispatch({ type: DETECTIONCLUSTER_LIST_ERROR });
				showError(error);
			});
	};

	const loadDetectionAlgorithms = () => {
		dispatch({ type: DETECTIONALG_LIST });
		request('get', `/detectionalgorithmtype?pipelineId=${pipelineId}&$sort[id]=1`)
			.then((response) => {
				if (response.status === 200) {
					dispatch({ type: DETECTIONALG_LIST_SUCCESS, payload: response.data });
				} else {
					dispatch({ type: DETECTIONALG_LIST_ERROR });
				}
			})
			.catch((error) => {
				dispatch({ type: DETECTIONALG_LIST_ERROR });
				showError(error);
			});
	};

	// Only fields that have a value are sent: the backend schema types are strict
	// (number / string, no null) and additionalProperties is false, so no pipelineId here -
	// the service reads it from the auth token.
	const buildPayload = (values) => {
		const payload = {
			pidsinfoId: values.pidsinfoId,
			detectionalgorithmtypeId: values.detectionalgorithmtypeId,
			status : 'New'
		};
		if (values.channelId != null) payload.channelId = values.channelId;
		if (values.starttime) payload.starttime = values.starttime.toISOString();
		if (values.endtime) payload.endtime = values.endtime.toISOString();
		if (values.seeddatetime) payload.seeddatetime = values.seeddatetime.toISOString();
		if (values.seedStartDistance != null && values.seedStartDistance !== '') payload.seedStartDistance = Number(values.seedStartDistance);
		if (values.seedEndDistance != null && values.seedEndDistance !== '') payload.seedEndDistance = Number(values.seedEndDistance);
		return payload;
	};

	const validateBeforeSave = (form) => {
		if (!form.getFieldValue('detectionalgorithmtypeId')) {
			Modal.warning({ title: t('PleaseSelectDetectionAlgorithm', 'Please Select detection algorithm') });
			return false;
		}
		const start = form.getFieldValue('starttime');
		const end = form.getFieldValue('endtime');
		if (start && end && end.isBefore(start)) {
			Modal.warning({ title: t('EndTimeBeforeStartTime', 'End time cannot be earlier than start time') });
			return false;
		}
		return true;
	};

	const handleAdd = async () => {
		if (!validateBeforeSave(addForm)) return;
		let values;
		try {
			values = await addForm.validateFields();
		} catch (e) {
			return;
		}

		dispatch({ type: ADD_DETECTIONCLUSTER });
		request('post', '/detectioncollections', buildPayload(values))
			.then((response) => {
				if (response.status === 200 || response.status === 201) {
					dispatch({ type: ADD_DETECTIONCLUSTER_SUCCESS, payload: response });
					Modal.success({ title: t('DetectionClusterAddedSuccessfully', 'Detection Cluster Added Successfully') });
					closeAddModal();
					loadClusters();
				} else {
					dispatch({ type: ADD_DETECTIONCLUSTER_ERROR });
					Modal.warning({ title: t('UnableToAddDetectionCluster', 'Unable To Add Detection Cluster') });
				}
			})
			.catch((error) => {
				dispatch({ type: ADD_DETECTIONCLUSTER_ERROR });
				showError(error);
			});
	};

	const handleUpdate = async () => {
		if (!validateBeforeSave(editForm)) return;
		let values;
		try {
			values = await editForm.validateFields();
		} catch (e) {
			return;
		}

		const id = data?.details?.id;
		const payload = buildPayload({ ...values, pidsinfoId: pidsFilter });

		dispatch({ type: UPDATE_DETECTIONCLUSTER });
		request('patch', `/detectioncollections/${id}?pidsinfoId=${pidsFilter}`, payload)
			.then((response) => {
				if (response.status === 200 || response.status === 201) {
					dispatch({ type: UPDATE_DETECTIONCLUSTER_SUCCESS, payload: response });
					Modal.success({ title: t('DetectionClusterUpdatedSuccessfully', 'Detection Cluster Updated Successfully') });
					closeEditModal();
					loadClusters();
				} else {
					dispatch({ type: UPDATE_DETECTIONCLUSTER_ERROR });
					Modal.warning({ title: t('UnableToUpdateDetectionCluster', 'Unable To Update Detection Cluster') });
				}
			})
			.catch((error) => {
				dispatch({ type: UPDATE_DETECTIONCLUSTER_ERROR });
				showError(error);
			});
	};

	const handleDelete = () => {
		const id = data?.details?.id;
		dispatch({ type: DELETE_DETECTIONCLUSTER });
		request('delete', `/detectioncollections/${id}?pidsinfoId=${pidsFilter}`)
			.then((response) => {
				if (response.status === 200) {
					dispatch({ type: DELETE_DETECTIONCLUSTER_SUCCESS, payload: response });
					setShowDeleteModal(false);
					// if the last row of a page was removed, step back one page
					if (data.clusters.length === 1 && currentPage > 1) {
						setCurrentPage(currentPage - 1);
					} else {
						loadClusters();
					}
				} else {
					dispatch({ type: DELETE_DETECTIONCLUSTER_ERROR });
				}
			})
			.catch((error) => {
				dispatch({ type: DELETE_DETECTIONCLUSTER_ERROR });
				showError(error);
			});
	};

	// ---------------------------------------------------------------- modal handlers
	const openAddModal = () => {
		getChannels(authtoken, pidsFilter);
		setShowAddModal(true);
	};

	const closeAddModal = () => {
		setShowAddModal(false);
		addForm.resetFields();
		getChannels(authtoken, pidsFilter); // restore channel list for the top filter
	};

	const openEditModal = (record) => {
		dispatch({ type: DETECTIONCLUSTER_DETAILS });
		if (!record) {
			dispatch({ type: DETECTIONCLUSTER_DETAILS_ERROR });
			return;
		}
		dispatch({ type: DETECTIONCLUSTER_DETAILS_SUCCESS, payload: record });
		getChannels(authtoken, pidsFilter);
		setShowEditModal(true);
	};

	const closeEditModal = () => {
		setShowEditModal(false);
		editForm.resetFields();
	};

	const openDeleteModal = (record) => {
		dispatch({ type: DETECTIONCLUSTER_DETAILS_SUCCESS, payload: record });
		setShowDeleteModal(true);
	};

	const handleFormPidsChange = (value, form) => {
		getChannels(authtoken, value);
		form.setFieldValue('channelId', undefined);
	};

	// ---------------------------------------------------------------- filter handlers
	const handlePidsFilter = (value) => {
		setPidsFilter(value);
		setChannelFilter(null);
		setCurrentPage(1);
	};
	const handleChannelFilter = (value) => {
		setChannelFilter(value);
		setCurrentPage(1);
	};
	const handleAlgFilter = (value) => {
		setAlgFilter(value);
		setCurrentPage(1);
	};
	const handleStatusFilter = (value) => {
		setStatusFilter(value);
		setCurrentPage(1);
	};
	const handleTableChange = (page, size) => {
		setCurrentPage(page);
		setPageSize(size);
	};

	// ---------------------------------------------------------------- effects

	
	useEffect(() => {
		if(featuresModule.trackMyVesselEnabled == false) {
			navigate('/dashboard');
		}
		window.scrollTo(0, 0);
		localStorage.setItem(dashboardtype.toUpperCase()+ '_ACTIVE_MENU_ITEM' ,4038);
		getPids(authtoken);
		loadDetectionAlgorithms();
	}, [authtoken]);

	useEffect(() => {
		if (pidsFilter) getChannels(authtoken, pidsFilter);
	}, [authtoken, pidsFilter]);

	useEffect(() => {
		loadClusters();
	}, [authtoken, pidsFilter, channelFilter, algFilter, statusFilter, currentPage, pageSize]);

	// ---------------------------------------------------------------- options
	const pids = (pidsData || [])
		.map((pid) => ({ value: pid.id, label: pid.name.toUpperCase() }))
		.sort((a, b) => a.value - b.value);

	const channels = (channelData || []).map((item) => ({ value: item.channelId, label: item.name }));

	const detalgOptions = (data.detalg || []).map((item) => ({ value: item.id, label: item.name }));

	const allLabel = t('ALL', 'ALL');
	const channelFilterOptions = [{ value: null, label: allLabel }, ...channels];
	const algFilterOptions = [{ value: null, label: allLabel }, ...detalgOptions];
	const statusFilterOptions = [{ value: null, label: allLabel }, ...STATUS_OPTIONS];

	// ---------------------------------------------------------------- table
	// const renderTags = (items, color = 'blue') =>
	// 	items && items.length > 0 ? (
	// 		<Space wrap size={[0, 4]}>
	// 			{items.map((value, index) => (
	// 				<Tag key={index} color={color}>{value}</Tag>
	// 			))}
	// 		</Space>
	// 	) : (
	// 		<Tag color="yellow">Nil</Tag>
	// 	);

	// const arrayColumn = (titleKey, titleFallback, field, color) => ({
	// 	title: t(titleKey, titleFallback),
	// 	dataIndex: field,
	// 	key: field,
	// 	width: 220,
	// 	render: (value) => renderTags(value, color),
	// });

	const columns = [
		{
			title: t('TrackType', 'Track Type'),
			dataIndex: 'trackType',
			key: 'trackType',
			width: 120,
		},
		{
			title: t('EventType', 'Event Type'),
			dataIndex: 'eventType',
			key: 'eventType',
			fixed: 'left',
			width: 150,
			render: (value) => (
				<Tag color={value ? 'blue' : 'yellow'} style={{ cursor: value ? 'pointer' : 'default' }}
					onClick={() => value && navigate('/eventtypes')}>
					{value ? value : 'Nil'}
				</Tag>
			),
		},
		{
			title: t('DetectionAlgorithm', 'Detection Algorithm'),
			dataIndex: 'detectionalgname',
			key: 'detectionalgname',
			width: 180,
			render: (value) =>
				value !== '_' ? (
					<a onClick={() => navigate('/detection-algorithm')} style={{ cursor: 'pointer', color: '#A81E1E' }}>
						{value}
					</a>
				) : value,
		},
		{
			title: t('PIDS', 'PIDS'),
			dataIndex: 'pidsname',
			key: 'pidsname',
			width: 100,
			render: (value) => {
				const fallback = pids.find((p) => p.value === pidsFilter)?.label;
				return value ? value.toString().toUpperCase() : fallback;
			},
		},
		{
			title: t('channelName', 'Channel'),
			dataIndex: 'channelname',
			key: 'channelname',
			width: 120,
		},
		{ title: t('StartTime', 'Start Time'), dataIndex: 'starttime', key: 'starttime', width: 180 },
		{ title: t('EndTime', 'End Time'), dataIndex: 'endtime', key: 'endtime', width: 180 },
		{ title: t('SeedDateTime', 'Trigger Date Time'), dataIndex: 'seeddatetime', key: 'seeddatetime', width: 180 },
		{
			title: t('TriggerStartDistance', 'Trigger Start Distance'),
			dataIndex: 'seedStartDistance',
			key: 'seedStartDistance',
			width: 150,
			render: (value) => (value ?? '-'),
		},
		{
			title: t('TriggerEndDistance', 'Trigger End Distance'),
			dataIndex: 'seedEndDistance',
			key: 'seedEndDistance',
			width: 150,
			render: (value) => (value ?? '-'),
		},
		{
			title: t('Status', 'Status'),
			dataIndex: 'status',
			key: 'status',
			width: 120,
			render: (value) => (
				<Tag color={value === 'Completed' ? '#179668' : value === 'Failed' ? 'red' :value === 'New' ? 'yellow' : 'purple'}>
					{value ? value : 'Nil'}
				</Tag>
			),
		},
	];

	if (isAdmin) {
		columns.push({
			title: t('Actions', 'Actions'),
			key: 'action',
			fixed: 'right',
			width: 100,
			render: (_, record) => (
				<Space size="middle">
					<a
						title={
							langdata?.ViewMyVessel
								? langdata.ViewMyVessel
								: 'View My Vessel'
						}
						onClick={() => {
							showMap(record);
						}}
					>
						<EyeOutlined />
					</a>
					<a title={t('EditDetectionCluster', 'Edit Detection Cluster')} onClick={() => openEditModal(record)}><EditOutlined /></a>
					<a title={t('DeleteDetectionCluster', 'Delete Detection Cluster')} onClick={() => openDeleteModal(record)}><DeleteOutlined /></a>
				</Space>
			),
		});
	}

	const showMap = (realtimedetection) => {
		// realtimedetection_filter_state = {
		// 	view,
		// 	regionId,
		// 	allocationId,
		// 	pidsFilter,
		// 	channelsdata,
		// 	chains,
		// 	algorithm,
		// 	trajectory,
		// 	timeSelection,
		// 	filterLastHour,
		// 	advDateRange,
		// 	startTime,
		// 	endTime,
		// 	appliedPidsFilter,
		// 	appliedChannelsdata,
		// 	appliedChains,
		// 	appliedAlgorithm,
		// 	appliedTrajectory,
		// 	appliedStartDate,
		// 	appliedEndDate,
		// };

		navigate(( '/shiptracker'),{ state: { data: [realtimedetection], path: 'realtimedetections', } });
	};

	// ---------------------------------------------------------------- shared form fields (add + edit)
	const renderFormFields = (form, isEdit) => (
		<>
			<Form.Item
				name="detectionalgorithmtypeId"
				label={t('DetectionAlgorithm', 'Detection Algorithm')}
				rules={[{ required: true, message: t('PleaseSelectDetectionAlgorithm', 'Please Select detection algorithm') }]}
			>
				<Select
					showSearch
					filterOption={filterOption}
					placeholder={t('PleaseSelectDetectionAlgorithm', 'Please Select detection algorithm')}
					options={detalgOptions}
				/>
			</Form.Item>

			<Row gutter={16}>
				<Col span={12}>
					<Form.Item
						name="pidsinfoId"
						label={t('PIDS', 'PIDS')}
						rules={[{ required: true, message: t('SelectPIDS', 'Select PIDS') }]}
					>
						<Select
							showSearch
							disabled={isEdit}
							filterOption={filterOption}
							placeholder={t('SelectPIDS', 'Select PIDS')}
							options={pids}
							onChange={(value) => handleFormPidsChange(value, form)}
						/>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item name="channelId" label={t('channelName', 'Channel')}>
						<Select
							showSearch
							allowClear
							filterOption={filterOption}
							placeholder={t('PleaseEnterChannelName', 'Please Enter Channel Name')}
							options={channels}
						/>
					</Form.Item>
				</Col>
			</Row>

			<Row gutter={16}>
				<Col span={12}>
					<Form.Item name="starttime" label={t('StartTime', 'Start Time')}>
						<DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm:ss" />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item name="endtime" label={t('EndTime', 'End Time')}>
						<DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm:ss" />
					</Form.Item>
				</Col>
			</Row>

			<Row gutter={16}>
				<Col span={12}>
					<Form.Item name="seeddatetime" label={t('SeedDateTime', 'Trigger Date Time')}>
						<DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm:ss" />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item name="seedStartDistance" label={t('SeedStartDistance', 'Tigger Start Distance')}>
						<InputNumber style={{ width: '100%' }} precision={0} min={0} />
					</Form.Item>
				</Col>
				
			</Row>

			<Row gutter={16}>
				<Col span={12}>
					<Form.Item name="seedEndDistance" label={t('SeedEndDistance', 'Trigger End Distance')}>
						<InputNumber style={{ width: '100%' }} precision={0} min={0} />
					</Form.Item>
				</Col>
			</Row>
		</>
	);

	const details = data.details;
	const editInitialValues = details
		? {
			detectionalgorithmtypeId: details.detectionalgorithmtypeId ?? undefined,
			pidsinfoId: pidsFilter,
			channelId: details.channelId ?? undefined,
			starttime: details.starttimeUTC ? dayjs(details.starttimeUTC) : null,
			endtime: details.endtimeUTC ? dayjs(details.endtimeUTC) : null,
			seeddatetime: details.seeddatetimeUTC ? dayjs(details.seeddatetimeUTC) : null,
			seedStartDistance: details.seedStartDistance ?? undefined,
			seedEndDistance: details.seedEndDistance ?? undefined,
			status: details.status || undefined,
		}
		: {};

	const infoContent = (
		<Card size="small" bordered={false} type="inner" style={{ width: 300 }}>
			<p>{t('DetectionClusterinfoContent', 'This module lists the detection clusters generated by each detection algorithm for a PIDS, including the seed point, source and fiber coordinates, and their review status. Clusters can be added, edited or deleted per PIDS.')}</p>
		</Card>
	);

	// ---------------------------------------------------------------- render
	return (
		<>
			<Content>
				<Row style={{ margin: '10px', paddingLeft: '10px', paddingRight: '10px', background: '#FFFFFF', alignItems: 'center' }}>
					<Col span={4}>
						<Row>
							<Space>
								<AppstoreOutlined style={{ fontSize: '30px' }} />
								<Title level={4} style={{ paddingTop: '10px' }}>
									{t('DetectionCollection', 'Detection Collection')}
								</Title>
							</Space>
						</Row>
					</Col>

					<Col span={20} style={{ display: 'flex', justifyContent: 'right' }}>
						<Row>
							<Col span={24}>
								<Tooltip placement="bottom" title={t('Filterbypids', 'Filter by pids')}>
									<Space>
										<Select
											style={{ minWidth: '200px', margin: '5px' }}
											showSearch
											filterOption={filterOption}
											placeholder={t('SelectPIDS', 'Select PIDS')}
											value={pidsFilter}
											options={pids}
											onSelect={handlePidsFilter}
										/>
									</Space>
								</Tooltip>

								<Tooltip placement="bottom" title={t('Filterbychannel', 'Filter by channel')}>
									<Space>
										<Select
											style={{ minWidth: '200px', margin: '5px' }}
											showSearch
											filterOption={filterOption}
											placeholder={t('SelectChannel', 'Select Channel')}
											value={channelFilter}
											options={channelFilterOptions}
											onSelect={handleChannelFilter}
										/>
									</Space>
								</Tooltip>

								<Tooltip placement="bottom" title={t('FilterbyDetectionAlgorithm', 'Filter by detection algorithm')}>
									<Space>
										<Select
											style={{ minWidth: '200px', margin: '5px' }}
											showSearch
											filterOption={filterOption}
											placeholder={t('SelectDetectionAlgorithm', 'Select Detection Algorithm')}
											value={algFilter}
											options={algFilterOptions}
											onSelect={handleAlgFilter}
										/>
									</Space>
								</Tooltip>

								<Tooltip placement="bottom" title={t('Filterbystatus', 'Filter by Status')}>
									<Space>
										<Select
											style={{ minWidth: '200px', margin: '5px' }}
											showSearch
											filterOption={filterOption}
											placeholder={t('SelectStatus', 'Select Status')}
											value={statusFilter}
											options={statusFilterOptions}
											onSelect={handleStatusFilter}
										/>
									</Space>
								</Tooltip>

								<Tooltip placement="bottom" title={t('Add', 'Add')}>
									<Space>
										{isAdmin ? (
											<Button type="primary" onClick={openAddModal} style={{ margin: '5px' }}>
												<PlusOutlined />
												{t('AddNew', 'Add New')}
											</Button>
										) : null}
									</Space>
								</Tooltip>

								<Space>&nbsp;&nbsp;</Space>

								<Space>
									<Typography>
										<Popover title={t('DetectionCluster', 'Detection Cluster')} content={infoContent} trigger="click">
											<Avatar size="small"><InfoOutlined /></Avatar>
										</Popover>
									</Typography>
								</Space>
							</Col>
						</Row>
					</Col>
				</Row>

				<Row style={{ margin: '10px', paddingLeft: '10px', paddingRight: '10px', background: '#FFFFFF', alignItems: 'center' }}>
					<Col span={24}>
						{data.clusters.length > 0 ? (
							<Table
								rowKey="id"
								scroll={{ x: 3200 }}
								columns={columns}
								dataSource={data.clusters}
								pagination={{
									current: data.pagination.current,
									pageSize: data.pagination.pageSize,
									total: data.pagination.total,
									showSizeChanger: false,
									pageSizeOptions: ['10', '20', '50'], // backend max page size is 50
									onChange: handleTableChange,
								}}
							/>
						) : (
							<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
						)}
					</Col>
				</Row>
			</Content>

			<Spin fullscreen spinning={data.pending} />

			{/* Add Detection Cluster */}
			<Modal
				title={t('AddDetectionCluster', 'Add Detection Cluster')}
				centered
				destroyOnClose
				maskClosable={false}
				open={showAddModal}
				onCancel={closeAddModal}
				footer={[
					<Space key="footer">
						<Button onClick={closeAddModal}>{t('CLOSE', 'CLOSE')}</Button>
						{isAdmin ? <Button type="primary" onClick={handleAdd}>{t('SAVE', 'SAVE')}</Button> : null}
					</Space>,
				]}
				width={800}
			>
				<Form layout="vertical" form={addForm} initialValues={{ pidsinfoId: pidsFilter }}>
					{renderFormFields(addForm, false)}
				</Form>
			</Modal>

			{/* Edit Detection Cluster */}
			<Modal
				title={t('EditDetectionCluster', 'Edit Detection Cluster')}
				centered
				destroyOnClose
				maskClosable={false}
				open={showEditModal}
				onCancel={closeEditModal}
				footer={[
					<Space key="footer">
						<Button onClick={closeEditModal}>{t('CLOSE', 'CLOSE')}</Button>
						<Button type="primary" onClick={handleUpdate}>{t('SAVE', 'SAVE')}</Button>
					</Space>,
				]}
				width={800}
			>
				<Form layout="vertical" form={editForm} initialValues={editInitialValues} key={details?.id}>
					{renderFormFields(editForm, true)}
				</Form>
			</Modal>

			{/* Delete Detection Cluster */}
			<Modal
				title={t('Confimdelete', 'Confirm delete')}
				open={showDeleteModal}
				onOk={handleDelete}
				onCancel={() => setShowDeleteModal(false)}
			>
				<Row style={{ marginTop: '10px', marginBottom: '10px', background: '#FFFFFF', alignItems: 'center' }}>
					<Col span={1}><DeleteOutlined style={{ fontSize: '14px' }} /></Col>
					<Col span={23}>{t('ConfirmDeleteDetectionCluster', 'Are you sure you want to delete this Detection Cluster ?')}</Col>
				</Row>
			</Modal>
		</>
	);
}